import { Component, Input, OnInit } from '@angular/core';
import { ForumPost } from 'app/forum/models/forum-post';
import { PostFormService } from 'app/forum/services/post-form.service';
import { DestroyComponent } from 'app/shared-ui/base/destroy/destroy.component';
import { MondoStep } from 'app/stepper/shared/model/MondoStep';
import { PostStepIndex } from 'app/stepper/shared/model/stepIndex';
import { PostStepGeneralComponent } from './steps/post-step-general/post-step-general.component';
import { PostStepTopicsComponent } from './steps/post-step-topics/post-step-topics.component';
import { PostStepViewComponent } from './steps/post-step-view/post-step-view.component';
import { MatDialog, MatDialogRef } from '@angular/material';
import { ConfirmDialogComponent } from 'app/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { take } from 'rxjs/operators';
import { MondoFormGroup } from 'app/core/mondo-form-builder';
import { PostService } from 'app/core/post.service';

@Component({
  selector: 'app-post-builder',
  templateUrl: './post-builder.component.html',
  styleUrls: ['./post-builder.component.scss'],
})
export class PostBuilderComponent extends DestroyComponent implements OnInit {
  @Input() post: ForumPost;
  @Input() updatingPost = false;

  steps: MondoStep[];
  initialValue: MondoFormGroup<ForumPost>;

  constructor(
    public postFormService: PostFormService,
    private postService: PostService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<PostBuilderComponent>
  ) {
    super();
  }

  ngOnInit() {
    this.postFormService.patchPost(this.post);
    this.initialValue = this.postFormService.getForm().getRawValue();
    this.setupSteps();

    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.cancel();
      }
      if (event.ctrlKey && event.code === 'Enter') {
        this.dialogRef.close();
      }
    });

    this.dialogRef.backdropClick().subscribe((result) => {
      this.cancel();
    });

    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((cancelled) => {
        if (cancelled) {
          return;
        } else {
          this.save();
        }
      });
  }

  save() {
    const post = this.postFormService.getForm().getRawValue();
    const postToUpdate = Object.assign(this.post, {});
    postToUpdate.title = post.title;
    postToUpdate.phone = post.phone;
    postToUpdate.uploads = post.uploads;
    postToUpdate.youtubeId = post.youtubeId;
    postToUpdate.email = post.email;
    postToUpdate.body = post.body;
    postToUpdate.topic = post.topic;
    postToUpdate.imageUrl = post.imageUrl;
    postToUpdate.imageLink = post.imageLink;
    postToUpdate.userTags = post.userTags;
    postToUpdate.taggedUserIds = post.taggedUserIds;
    // postToUpdate.teaser = this.group.permissions.premium;

    if (this.updatingPost) {
      this.postService.updatePost(postToUpdate);
    } else {
      if (post.plannedAt) {
        postToUpdate.plannedAt = new Date(post.plannedAt);
        this.postService.createPlannedPost(postToUpdate);
      } else {
        this.postService.createPost(postToUpdate);
      }
    }
  }

  private hasChanges(): boolean {
    const currentValue = this.postFormService.getForm().getRawValue();
    return JSON.stringify(currentValue) !== JSON.stringify(this.initialValue);
  }

  cancel() {
    if (!this.hasChanges()) {
      this.dialogRef.close(true);
      return;
    }

    const innerDialogRef = this.dialog.open(ConfirmDialogComponent, {});
    innerDialogRef.componentInstance.title = this.updatingPost
      ? 'discardChangesPostUpdateTitle'
      : 'discardChangesPostCreateTitle';
    innerDialogRef.componentInstance.text = this.updatingPost
      ? 'discardChangesPostUpdateBody'
      : 'discardChangesPostCreateBody';
    innerDialogRef.componentInstance.yesText = 'yes';
    innerDialogRef.componentInstance.noText = 'no';

    return new Promise((resolve) => {
      innerDialogRef
        .afterClosed()
        .pipe(take(1))
        .toPromise()
        .then((cancel) => {
          if (cancel) {
            this.dialogRef.close(cancel);
          }
        });
    });
  }

  private setupSteps() {
    const general = new MondoStep(
      PostStepIndex.general,
      PostStepGeneralComponent,
      'generalInformation',
      new Map().set('postKey', this.post.key).set('forumId', this.post.forumId),
      [true, false]
    );
    const topics = new MondoStep(
      PostStepIndex.topics,
      PostStepTopicsComponent,
      'topics',
      new Map(),
      [false, false]
    );
    const view = new MondoStep(
      PostStepIndex.view,
      PostStepViewComponent,
      'Preview',
      new Map().set('post', this.post).set('updatingPost', this.updatingPost),
      [false, true]
    );

    this.steps = [general, topics, view];
  }

  onStepChanged(index: PostStepIndex) {}
}
